import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Filter,
  MapPin,
  Shield,
  MoreVertical,
  ExternalLink,
  IndianRupee,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// Import the modal component we created
import AddSiteModal from "./AddSiteModal";

export default function SitesPage() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // 1. State to control the modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchSites();
  }, []);

  async function fetchSites() {
    try {
      setLoading(true);

      // 1. Fetch sites and their related daily_logs total_expenses in one go
      const { data, error } = await supabase
        .from("sites")
        .select(
          `
        *,
        daily_logs (
          total_expenses
        )
      `,
        )
        .order("created_at", { ascending: false });

      if (error) throw error;

      // 2. Map the data to calculate net_spend from the nested logs array
      const sitesWithSpend = data.map((site) => {
        const totalSpend =
          site.daily_logs?.reduce(
            (sum, log) => sum + Number(log.total_expenses || 0),
            0,
          ) || 0;

        return {
          ...site,
          net_spend: totalSpend,
        };
      });

      setSites(sitesWithSpend);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Could not load sites.");
    } finally {
      setLoading(false);
    }
  }

  const filteredSites = sites.filter((site) => {
    const matchesSearch =
      site.site_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterStatus === "all" || site.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6 p-8 animate-in fade-in duration-500">
      {/* 2. Integrate the Modal Component */}
      <AddSiteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onRefresh={fetchSites}
      />

      {/* Header & Quick Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            Site Inventory
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">
            Manage HPCL Retail Outlets and Infrastructure assets.
          </p>
        </div>

        {/* 3. Attach the onClick handler to open the modal */}
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-[#0054A6] text-white rounded-xl font-bold shadow-lg hover:bg-blue-700 transition-all active:scale-95"
        >
          <Plus size={20} /> Add New Site
        </button>
      </div>

      {/* Filters & Search */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-8 relative">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Search by site name or location..."
            className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl focus:ring-2 focus:ring-[#0054A6] outline-none transition-all dark:text-white text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="md:col-span-4">
          <div className="relative">
            <Filter
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <select
              className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl appearance-none outline-none dark:text-white text-sm font-bold"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Project Phases</option>
              <option value="active">Active (Operational)</option>
              <option value="construction">Under Construction</option>
              <option value="MB_Running">MB Running</option>
              <option value="MB_Completed">MB Completed</option>
              <option value="invoices">Invoices Stage</option>
              <option value="Payment_Received">Payment Received</option>
            </select>
          </div>
        </div>
      </div>

      {/* Sites Grid */}
      {loading && sites.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-64 bg-slate-100 dark:bg-slate-900/50 animate-pulse rounded-[2rem]"
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSites.map((site) => (
            <SiteCard key={site.id} site={site} />
          ))}
          {filteredSites.length === 0 && (
            <div className="col-span-full py-20 text-center bg-white dark:bg-slate-900 rounded-[3rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
              <MapPin className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500 font-bold">
                No sites found matching your criteria.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="mt-4 text-[#0054A6] font-bold text-sm hover:underline"
              >
                Add your first site now
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SiteCard({ site }) {
  const navigate = useNavigate();

  // Updated color mapping to match your new status options
  const statusColors = {
    active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    construction: "bg-orange-100 text-orange-700 border-orange-200",
    completed: "bg-blue-100 text-blue-700 border-blue-200",
    MB_Running: "bg-purple-100 text-purple-700 border-purple-200",
    MB_Completed: "bg-indigo-100 text-indigo-700 border-indigo-200",
    invoices: "bg-pink-100 text-pink-700 border-pink-200",
    Payment_Received: "bg-cyan-100 text-cyan-700 border-cyan-200",
  };

  // Dynamic border color for the card itself based on status
  const borderStyles = {
    active: "hover:border-b-emerald-500",
    construction: "hover:border-b-orange-500",
    MB_Running: "hover:border-b-purple-500",
    MB_Completed: "hover:border-b-indigo-500",
    invoices: "hover:border-b-pink-500",
    Payment_Received: "hover:border-b-cyan-500",
    completed: "hover:border-b-blue-500",
  };

  return (
    <div
      onClick={() => navigate(`/sites/${site.id}`)}
      className={`cursor-pointer bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] p-6 hover:shadow-xl transition-all group border-b-4 ${borderStyles[site.status] || "hover:border-b-slate-400"}`}
    >
      <div className="flex justify-between items-start mb-4">
        <div
          className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${statusColors[site.status] || "bg-slate-100"}`}
        >
          {site.status?.replace("_", " ")}
        </div>
        <button className="text-slate-400 hover:text-slate-600 transition-colors">
          <MoreVertical size={18} />
        </button>
      </div>

      <h3 className="text-xl font-black text-slate-900 dark:text-white mb-1 transition-colors uppercase truncate">
        {site.site_name}
      </h3>
      <p className="text-xs text-slate-500 flex items-center gap-1.5 mb-6">
        <MapPin size={12} className="text-[#EE3124]" /> {site.location}
      </p>

      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* REPLACED DU WITH NET SPEND */}
        <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700">
          <p className="text-[9px] font-black text-slate-400 uppercase mb-1 tracking-tighter">
            Net Spend
          </p>
          <div className="flex items-center gap-1 text-slate-900 dark:text-slate-200 font-black text-sm">
            <IndianRupee size={12} className="text-emerald-600" />
            {site.net_spend?.toLocaleString() || 0}
          </div>
        </div>

        <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700">
          <p className="text-[9px] font-black text-slate-400 uppercase mb-1 tracking-tighter">
            Site Code
          </p>
          <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200 font-bold text-sm">
            <Shield size={14} className="text-[#0054A6]" />
            {site.ro_code || "N/A"}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-900 hover:text-white transition-all flex items-center justify-center gap-2">
          Manage Project <ExternalLink size={14} />
        </button>
      </div>
    </div>
  );
}
